/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListBusinessMemoriesRequest,
  ListBusinessMemoriesSecurity,
} from "../models/operations/listbusinessmemories.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildListBusinessMemoriesInfiniteQuery,
  buildListBusinessMemoriesQuery,
  ListBusinessMemoriesInfiniteQueryData,
  ListBusinessMemoriesPageParams,
  ListBusinessMemoriesQueryData,
  prefetchListBusinessMemories,
  prefetchListBusinessMemoriesInfinite,
  queryKeyListBusinessMemories,
  queryKeyListBusinessMemoriesInfinite,
} from "./listBusinessMemories.core.js";
export {
  buildListBusinessMemoriesInfiniteQuery,
  buildListBusinessMemoriesQuery,
  type ListBusinessMemoriesInfiniteQueryData,
  type ListBusinessMemoriesPageParams,
  type ListBusinessMemoriesQueryData,
  prefetchListBusinessMemories,
  prefetchListBusinessMemoriesInfinite,
  queryKeyListBusinessMemories,
  queryKeyListBusinessMemoriesInfinite,
};

export type ListBusinessMemoriesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listBusinessMemories businessMemories
 *
 * @remarks
 * List memories extracted for the active project. Requires organization admin.
 */
export function useListBusinessMemories(
  request?: ListBusinessMemoriesRequest | undefined,
  security?: ListBusinessMemoriesSecurity | undefined,
  options?: QueryHookOptions<
    ListBusinessMemoriesQueryData,
    ListBusinessMemoriesQueryError
  >,
): UseQueryResult<
  ListBusinessMemoriesQueryData,
  ListBusinessMemoriesQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildListBusinessMemoriesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listBusinessMemories businessMemories
 *
 * @remarks
 * List memories extracted for the active project. Requires organization admin.
 */
export function useListBusinessMemoriesSuspense(
  request?: ListBusinessMemoriesRequest | undefined,
  security?: ListBusinessMemoriesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ListBusinessMemoriesQueryData,
    ListBusinessMemoriesQueryError
  >,
): UseSuspenseQueryResult<
  ListBusinessMemoriesQueryData,
  ListBusinessMemoriesQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildListBusinessMemoriesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listBusinessMemories businessMemories
 *
 * @remarks
 * List memories extracted for the active project. Requires organization admin.
 */
export function useListBusinessMemoriesInfinite(
  request?: ListBusinessMemoriesRequest | undefined,
  security?: ListBusinessMemoriesSecurity | undefined,
  options?: InfiniteQueryHookOptions<
    ListBusinessMemoriesInfiniteQueryData,
    ListBusinessMemoriesQueryError
  >,
): UseInfiniteQueryResult<
  InfiniteData<
    ListBusinessMemoriesInfiniteQueryData,
    ListBusinessMemoriesPageParams
  >,
  ListBusinessMemoriesQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    ListBusinessMemoriesInfiniteQueryData,
    ListBusinessMemoriesQueryError,
    InfiniteData<
      ListBusinessMemoriesInfiniteQueryData,
      ListBusinessMemoriesPageParams
    >,
    QueryKey,
    ListBusinessMemoriesPageParams
  >({
    ...buildListBusinessMemoriesInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * listBusinessMemories businessMemories
 *
 * @remarks
 * List memories extracted for the active project. Requires organization admin.
 */
export function useListBusinessMemoriesInfiniteSuspense(
  request?: ListBusinessMemoriesRequest | undefined,
  security?: ListBusinessMemoriesSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    ListBusinessMemoriesInfiniteQueryData,
    ListBusinessMemoriesQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<
    ListBusinessMemoriesInfiniteQueryData,
    ListBusinessMemoriesPageParams
  >,
  ListBusinessMemoriesQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    ListBusinessMemoriesInfiniteQueryData,
    ListBusinessMemoriesQueryError,
    InfiniteData<
      ListBusinessMemoriesInfiniteQueryData,
      ListBusinessMemoriesPageParams
    >,
    QueryKey,
    ListBusinessMemoriesPageParams
  >({
    ...buildListBusinessMemoriesInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setListBusinessMemoriesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      cursor?: string | undefined;
      contentScope?: string | undefined;
      contentScopeNamespace?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: ListBusinessMemoriesQueryData,
): ListBusinessMemoriesQueryData | undefined {
  const key = queryKeyListBusinessMemories(...queryKeyBase);

  return client.setQueryData<ListBusinessMemoriesQueryData>(key, data);
}

export function invalidateListBusinessMemories(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      cursor?: string | undefined;
      contentScope?: string | undefined;
      contentScopeNamespace?: string | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "businessMemories", "list", ...queryKeyBase],
  });
}

export function invalidateAllListBusinessMemories(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "businessMemories", "list"],
  });
}
